import React, { useCallback, useState } from 'react';
import { Panel} from 'rsuite';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';

import NavigationBar from '../Shared/NavigationBar';
import RulesDisclaimer from '../Shared/RulesDisclaimer';
import useScrollToTop from '../../helpers/useScrollToTop';
import RulesSidebarNav from './RulesSidebarNav';
import { ZONES, ZONE_IDS } from '../../helpers/rulesData';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const ACTIVE_ZONES = ZONES.filter((z) => z.id !== ZONE_IDS.ANTE);

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 24,
  headerMarginBottom: 24,
  titleMarginBottom: 8,
  sectionMarginBottom: 32,
  panelMarginBottom: 16,
  sidebarWidth: 220,
  sidebarGap: 32,
  sidebarPadding: 16,
  sidebarBorderRadius: 6,
  sidebarTitleMarginBottom: 8,
  sidebarItemPadding: '6px 0',
  stickyTop: 20,
  backLinkMarginBottom: 16,
  backLinkPadding: '10px 0',
  introPadding: 20,
  introBorderRadius: 8,
  paragraphMarginBottom: 12,
  diagramPadding: 24,
  diagramBorderRadius: 8,
  diagramNodePadding: '8px 14px',
  diagramNodeBorderRadius: 6,
  diagramNodeMarginBottom: 6,
  diagramArrowPadding: '2px 0',
  diagramArrowFontSize: 11,
  diagramGridGap: 16,
  diagramColumnMinWidth: 140,
  keyRulePadding: '6px 0',
  keyRuleBulletMarginRight: 8,
  transitionPadding: '4px 0',
  transitionBulletMarginRight: 8,
  visibilityBadgePadding: '2px 8px',
  visibilityBadgeBorderRadius: 4,
  visibilityBadgeMarginLeft: 10,
  zoneCardPadding: 16,
  zoneCardBorderRadius: 6,
  zoneCardMarginBottom: 16,
  zoneNameMarginBottom: 6,
  zoneDescriptionMarginBottom: 14,
  transitionsHeadingMarginBottom: 6,
  specialTopicPadding: 20,
  specialTopicBorderRadius: 8,
  specialTopicMarginBottom: 16,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  subtitle: 15,
  subtitleMobile: 14,
  sidebarTitle: 14,
  sidebarTitleWeight: 600,
  sidebarTitleLetterSpacing: 1,
  sidebarItem: 14,
  sectionHeading: 22,
  sectionHeadingWeight: 600,
  zoneName: 18,
  zoneNameWeight: 600,
  zoneDescription: 14,
  zoneDescriptionLineHeight: 1.7,
  keyRuleText: 14,
  keyRuleLineHeight: 1.6,
  transitionText: 14,
  transitionLineHeight: 1.5,
  transitionsHeading: 14,
  transitionsHeadingWeight: 600,
  transitionsHeadingLetterSpacing: 0.5,
  visibilityBadge: 14,
  visibilityBadgeWeight: 600,
  introText: 14,
  introTextLineHeight: 1.7,
  backLink: 14,
  diagramNode: 14,
  diagramNodeWeight: 600,
  diagramArrow: 14,
  specialHeading: 18,
  specialHeadingWeight: 600,
  specialText: 14,
  specialTextLineHeight: 1.7,
};

const buildColors = (theme) => {
  const t = getThemeColors(theme);
  return {
    title: t.textPrimary,
    subtitle: t.textMuted,
    sidebarBg: t.cardBackground,
    sidebarBorder: t.borderPrimary,
    sidebarTitle: t.accentPrimary,
    sidebarItem: t.textSecondary,
    sidebarItemHover: t.accentPrimary,
    panelBg: t.cardBackground,
    panelBorder: t.borderPrimary,
    zoneName: t.textPrimary,
    zoneDescription: t.textSecondary,
    zoneCardBg: t.backgroundSecondary,
    zoneCardBorder: t.borderPrimary,
    keyRuleText: t.textSecondary,
    keyRuleBullet: t.accentPrimary,
    transitionText: t.textMuted,
    transitionBullet: '#52b788',
    transitionsHeading: '#52b788',
    visibilityHiddenBg: '#4a1a1a',
    visibilityHiddenText: '#e07070',
    visibilityPrivateBg: '#4a3a1a',
    visibilityPrivateText: '#e0c060',
    visibilityPublicBg: '#1b4332',
    visibilityPublicText: '#52b788',
    introBg: t.backgroundSecondary,
    introBorder: t.borderPrimary,
    introText: t.textSecondary,
    diagramBg: t.cardBackground,
    diagramBorder: t.borderPrimary,
    diagramNodeBg: theme === 'light' ? '#e3f2fd' : '#1e2a4a',
    diagramNodeText: t.textPrimary,
    diagramNodeBorder: t.accentSecondary,
    diagramArrowText: t.textFaint,
    backLink: t.accentPrimary,
    breadcrumbSeparator: t.textFaint,
    breadcrumbLink: t.accentPrimary,
    breadcrumbActive: t.textSecondary,
    sectionHeading: t.textPrimary,
    specialBg: t.cardBackground,
    specialBorder: t.borderPrimary,
    specialText: t.textSecondary,
    specialHeading: t.textPrimary,
  };
};


const VISIBILITY_STYLES = {
  Hidden: { bg: '#4a1a1a', color: '#e07070', icon: '🔒' },
  Private: { bg: '#4a3a1a', color: '#e0c060', icon: '👁️' },
  Public: { bg: '#1b4332', color: '#52b788', icon: '🌐' },
};

const ZONE_TRANSITIONS_DIAGRAM = [
  { from: 'Library', to: 'Hand', label: 'Draw' },
  { from: 'Hand', to: 'Stack', label: 'Cast' },
  { from: 'Hand', to: 'Battlefield', label: 'Play land' },
  { from: 'Stack', to: 'Battlefield', label: 'Resolve (permanent)' },
  { from: 'Stack', to: 'Graveyard', label: 'Resolve (instant/sorcery)' },
  { from: 'Battlefield', to: 'Graveyard', label: 'Destroy / Sacrifice' },
  { from: 'Battlefield', to: 'Exile', label: 'Exile' },
  { from: 'Battlefield', to: 'Hand', label: 'Bounce' },
  { from: 'Graveyard', to: 'Battlefield', label: 'Reanimate' },
  { from: 'Graveyard', to: 'Exile', label: 'Delve / Escape' },
  { from: 'Exile', to: 'Battlefield', label: 'Return from exile' },
  { from: 'Command', to: 'Stack', label: 'Cast commander' },
];

const SPECIAL_TOPICS = [
  {
    id: 'zone-change-triggers',
    title: 'Zone-Change Triggers',
    content:
      'Many abilities trigger when a card changes zones. The most common are:\n\n' +
      '• "When [this] enters the battlefield": triggers when a permanent moves to the battlefield from any zone.\n' +
      '• "When [this] dies": triggers specifically when a creature moves from the battlefield to the graveyard. "Dies" only refers to this specific zone change.\n' +
      '• "When [this] leaves the battlefield": triggers when a permanent moves from the battlefield to any other zone (graveyard, exile, hand, library, command zone).\n' +
      '• "Whenever a card is put into your graveyard from anywhere": triggers on any zone → graveyard transition.\n\n' +
      'Zone-change triggers use the stack and can be responded to. The trigger "sees" the event and goes on the stack the next time a player would receive priority.',
  },
  {
    id: 'leaves-battlefield',
    title: 'Leaves the Battlefield Rules',
    content:
      'When a permanent leaves the battlefield, it becomes a new object in its new zone with no memory of its previous existence. This means:\n\n' +
      '• Auras and Equipment attached to it fall off (Auras go to the graveyard, Equipment stays on the battlefield unattached).\n' +
      '• Counters on it are removed.\n' +
      '• Any continuous effects it was generating end.\n' +
      '• If it returns to the battlefield later (e.g., blinked by Restoration Angel), it is a completely new permanent. It does not remember any previous state, attachments, or counters.\n\n' +
      'This "new object" rule is fundamental to understanding flicker/blink effects and temporary exile.',
  },
  {
    id: 'outside-game',
    title: 'Outside the Game & Wish Effects',
    content:
      'The concept of "outside the game" refers to cards not currently in any game zone. In casual play, "outside the game" means your entire collection. In tournament play, "outside the game" means your sideboard.\n\n' +
      'Cards like Burning Wish, Living Wish, and Glittering Wish let you bring a card from "outside the game" into your hand. The companion mechanic also brings a card from outside the game.\n\n' +
      'Cards that have been exiled are NOT outside the game. They are in the exile zone and cannot be found by wish effects. Cards in the command zone are also not outside the game.',
  },
];

const SIDEBAR_ITEMS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'diagram', label: 'Zone Transitions' },
  ...ACTIVE_ZONES.map((zone) => ({
    id: zone.id,
    label: zone.name,
  })),
  { id: 'special-topics', label: 'Special Topics' },
];

const ZonesReference = () => {
  const { preferences } = usePreferences();
  const COLORS = buildColors(preferences.theme);

  const [activeId, setActiveId] = useState('intro');

  useScrollToTop();

  const scrollToSection = useCallback((sectionId) => {
    setActiveId(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [setActiveId]);


  return (
    <>
      <NavigationBar />
      <div
        style={{
          maxWidth: SPACING.containerMaxWidth,
          margin: '0 auto',
          padding: isMobile ? SPACING.containerPaddingMobile : SPACING.containerPadding,
        }}
      >
        {/* Back link */}
        <div style={{ marginBottom: SPACING.backLinkMarginBottom }}>
          <Link to="/rules" style={{ color: COLORS.backLink, fontSize: FONT.backLink, textDecoration: 'none' }}>
            ← Back to Rules Reference
          </Link>
        </div>


        {/* Header */}
        <div style={{ marginTop: SPACING.headerMarginTop, marginBottom: SPACING.headerMarginBottom }}>
          <h1 style={{ fontSize: isMobile ? FONT.titleMobile : FONT.title, color: COLORS.title, marginBottom: SPACING.titleMarginBottom }}>
            🗺️ Zones
          </h1>
          <div style={{ fontSize: isMobile ? FONT.subtitleMobile : FONT.subtitle, color: COLORS.subtitle }}>
            Where cards exist during a game and the rules governing zone transitions
          </div>
        </div>

        {/* Layout: sidebar + content */}
        <div style={{ display: 'flex', gap: isMobile ? 0 : SPACING.sidebarGap, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Sidebar Navigation - responsive layout handled by component */}
          <RulesSidebarNav
            sections={SIDEBAR_ITEMS}
            activeId={activeId}
            onSelect={scrollToSection}
          />

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Introduction */}
            <div id="intro" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <div
                style={{
                  backgroundColor: COLORS.introBg,
                  border: `1px solid ${COLORS.introBorder}`,
                  borderRadius: SPACING.introBorderRadius,
                  padding: SPACING.introPadding,
                }}
              >
                <div style={{ fontSize: FONT.introText, color: COLORS.introText, lineHeight: FONT.introTextLineHeight }}>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    In Magic: The Gathering, cards exist in one of several <strong style={{ color: COLORS.title }}>zones</strong>.
                    Each zone has rules about visibility (who can see the cards), ordering, and what can be done with
                    cards in that zone.
                  </p>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    The seven active zones are: <strong style={{ color: COLORS.title }}>Library</strong>, <strong style={{ color: COLORS.title }}>Hand</strong>, <strong style={{ color: COLORS.title }}>Battlefield</strong>, <strong style={{ color: COLORS.title }}>Graveyard</strong>, <strong style={{ color: COLORS.title }}>Stack</strong>, <strong style={{ color: COLORS.title }}>Exile</strong>, and <strong style={{ color: COLORS.title }}>Command Zone</strong>.
                  </p>
                  <p style={{ margin: 0 }}>
                    Cards move between zones through game actions like drawing, casting, resolving, destroying, and exiling.
                    Many abilities trigger when a card changes zones, making zone transitions a key part of gameplay strategy.
                  </p>
                </div>
              </div>
            </div>

            {/* Zone Transitions Diagram */}
            <div id="diagram" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: COLORS.sectionHeading, marginBottom: SPACING.panelMarginBottom }}>
                Common Zone Transitions
              </h2>
              <div
                style={{
                  backgroundColor: COLORS.diagramBg,
                  border: `1px solid ${COLORS.diagramBorder}`,
                  borderRadius: SPACING.diagramBorderRadius,
                  padding: SPACING.diagramPadding,
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : `repeat(auto-fill, minmax(${SPACING.diagramColumnMinWidth}px, 1fr))`,
                    gap: SPACING.diagramGridGap,
                  }}
                >
                  {ZONE_TRANSITIONS_DIAGRAM.map((t, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: COLORS.diagramNodeBg,
                          border: `1px solid ${COLORS.diagramNodeBorder}`,
                          borderRadius: SPACING.diagramNodeBorderRadius,
                          padding: SPACING.diagramNodePadding,
                          fontSize: FONT.diagramNode,
                          fontWeight: FONT.diagramNodeWeight,
                          color: COLORS.diagramNodeText,
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        {t.from}
                      </div>
                      <div style={{ padding: SPACING.diagramArrowPadding, fontSize: SPACING.diagramArrowFontSize, color: COLORS.diagramArrowText, textAlign: 'center' }}>
                        ▼ {t.label}
                      </div>
                      <div
                        style={{
                          backgroundColor: COLORS.diagramNodeBg,
                          border: `1px solid ${COLORS.diagramNodeBorder}`,
                          borderRadius: SPACING.diagramNodeBorderRadius,
                          padding: SPACING.diagramNodePadding,
                          fontSize: FONT.diagramNode,
                          fontWeight: FONT.diagramNodeWeight,
                          color: COLORS.diagramNodeText,
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        {t.to}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Zone Cards */}
            {ACTIVE_ZONES.map((zone) => {
              const vis = VISIBILITY_STYLES[zone.visibility] || VISIBILITY_STYLES.Public;
              return (
                <div
                  key={zone.id}
                  id={zone.id}
                  style={{ marginBottom: SPACING.sectionMarginBottom }}
                >
                  <Panel
                    bordered
                    style={{ backgroundColor: COLORS.panelBg, borderColor: COLORS.panelBorder }}
                  >
                    {/* Zone header */}
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: SPACING.zoneNameMarginBottom }}>
                      <h3 style={{ fontSize: FONT.zoneName, fontWeight: FONT.zoneNameWeight, color: COLORS.zoneName, margin: 0, marginRight: SPACING.visibilityBadgeMarginLeft }}>
                        {zone.name}
                      </h3>
                      <span
                        style={{
                          fontSize: FONT.visibilityBadge,
                          fontWeight: FONT.visibilityBadgeWeight,
                          color: vis.color,
                          backgroundColor: vis.bg,
                          padding: SPACING.visibilityBadgePadding,
                          borderRadius: SPACING.visibilityBadgeBorderRadius,
                        }}
                      >
                        {vis.icon} {zone.visibility}{zone.ordered ? ' · Ordered' : ''}
                      </span>
                    </div>

                    {/* Description */}
                    <div
                      style={{
                        fontSize: FONT.zoneDescription,
                        color: COLORS.zoneDescription,
                        lineHeight: FONT.zoneDescriptionLineHeight,
                        marginBottom: SPACING.zoneDescriptionMarginBottom,
                      }}
                    >
                      {zone.description}
                    </div>

                    {/* Key Rules */}
                    {zone.keyRules && zone.keyRules.length > 0 && (
                      <div style={{ marginBottom: zone.commonTransitions && zone.commonTransitions.length > 0 ? SPACING.panelMarginBottom : 0 }}>
                        {zone.keyRules.map((rule, rIdx) => (
                          <div
                            key={`${zone.id}-rule-${rIdx}`}
                            style={{
                              display: 'flex',
                              padding: SPACING.keyRulePadding,
                              fontSize: FONT.keyRuleText,
                              color: COLORS.keyRuleText,
                              lineHeight: FONT.keyRuleLineHeight,
                            }}
                          >
                            <span style={{ color: COLORS.keyRuleBullet, marginRight: SPACING.keyRuleBulletMarginRight, flexShrink: 0 }}>•</span>
                            <span>{rule}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Common Transitions */}
                    {zone.commonTransitions && zone.commonTransitions.length > 0 && (
                      <div>
                        <div style={{
                          fontSize: FONT.transitionsHeading,
                          fontWeight: FONT.transitionsHeadingWeight,
                          color: COLORS.transitionsHeading,
                          textTransform: 'uppercase',
                          letterSpacing: FONT.transitionsHeadingLetterSpacing,
                          marginBottom: SPACING.transitionsHeadingMarginBottom,
                        }}>
                          Common Transitions
                        </div>
                        {zone.commonTransitions.map((transition, tIdx) => (
                          <div
                            key={`${zone.id}-trans-${tIdx}`}
                            style={{
                              display: 'flex',
                              padding: SPACING.transitionPadding,
                              fontSize: FONT.transitionText,
                              color: COLORS.transitionText,
                              lineHeight: FONT.transitionLineHeight,
                            }}
                          >
                            <span style={{ color: COLORS.transitionBullet, marginRight: SPACING.transitionBulletMarginRight, flexShrink: 0 }}>→</span>
                            <span>{transition}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Panel>
                </div>
              );
            })}

            {/* Special Topics */}
            <div id="special-topics" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: COLORS.sectionHeading, marginBottom: SPACING.panelMarginBottom }}>
                Special Topics
              </h2>

              {SPECIAL_TOPICS.map((topic) => (
                <div
                  key={topic.id}
                  style={{
                    backgroundColor: COLORS.specialBg,
                    border: `1px solid ${COLORS.specialBorder}`,
                    borderRadius: SPACING.specialTopicBorderRadius,
                    padding: SPACING.specialTopicPadding,
                    marginBottom: SPACING.specialTopicMarginBottom,
                  }}
                >
                  <h3 style={{ fontSize: FONT.specialHeading, fontWeight: FONT.specialHeadingWeight, color: COLORS.specialHeading, marginBottom: SPACING.paragraphMarginBottom }}>
                    {topic.title}
                  </h3>
                  <div
                    style={{
                      fontSize: FONT.specialText,
                      color: COLORS.specialText,
                      lineHeight: FONT.specialTextLineHeight,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {topic.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <RulesDisclaimer />
      </div>
    </>
  );
};

export default ZonesReference;
